import React, { useState, useEffect } from "react";
import { getDepartments, getDoctors, addVisit } from "../../api/apiService";
import { sanitizeText } from "../../utils/inputValidation";
import { useAuth } from "../../context/AuthContext";
import { PERMISSIONS } from "../../constants/permissions";

const visitTypes = [
  "General OPD",
  "IPD",
  "Emergency",
  "Teleconsultation",
  "Other",
];

const SearchIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: "#9CA3AF" }}>
    <circle cx="11" cy="11" r="8"></circle>
    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
  </svg>
);

const SearchIconWhite = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: "#FFF" }}>
    <circle cx="11" cy="11" r="8"></circle>
    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
  </svg>
);

const CloseIconSmall = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: "#9CA3AF" }}>
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const RightArrow = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: "#111827" }}>
    <line x1="5" y1="12" x2="19" y2="12"></line>
    <polyline points="12 5 19 12 12 19"></polyline>
  </svg>
);

const CreateVisitModal = ({ isOpen, onClose, patientId, patientName, patientMeta, onVisitCreated }) => {
  const { can, user } = useAuth();
  const canAddVisit = can(PERMISSIONS.OPD_ADD_VISIT);
  const [departments, setDepartments] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loadingDepts, setLoadingDepts] = useState(false);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({
    visitType: "",
    description: "",
    departmentId: "",
    doctorId: "",
  });
  const [searchTerm, setSearchTerm] = useState("");

  // Reset form and fetch departments each time modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({ visitType: "", description: "", departmentId: "", doctorId: "" });
      setDoctors([]);
      setError("");
      setSearchTerm("");
      
      const fetchData = async () => {
        try {
          setLoadingDepts(true);
          setLoadingDoctors(true);
          
          const [deptRes, docRes] = await Promise.all([
            getDepartments(),
            getDoctors()
          ]);
          
          const deptList = deptRes?.data?.departments || deptRes?.departments || deptRes?.data || [];
          setDepartments(Array.isArray(deptList) ? deptList : []);
          
          const docList = docRes?.data?.doctors || docRes?.doctors || docRes?.data || [];
          setDoctors(Array.isArray(docList) ? docList : []);
          
        } catch (err) {
          console.error("Error fetching data:", err);
          setError("Failed to load departments or doctors.");
        } finally {
          setLoadingDepts(false);
          setLoadingDoctors(false);
        }
      };
      
      fetchData();
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Auto-select department if a doctor is selected (optional but helpful)
    if (name === "doctorId") {
      const selectedDoc = doctors.find(d => d._id === value);
      if (selectedDoc && selectedDoc.department && selectedDoc.department._id) {
        setFormData(prev => ({ 
          ...prev, 
          doctorId: value,
          departmentId: selectedDoc.department._id 
        }));
        return;
      }
    }
    
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!canAddVisit) {
      setError("You do not have permission to create patient visits.");
      return;
    }
    if (!formData.visitType) { setError("Please select a visit type."); return; }
    if (!formData.departmentId) { setError("Please select a department."); return; }
    if (!formData.doctorId) { setError("Please select a doctor."); return; }

    try {
      setSubmitting(true);
      const selectedDoctor = doctors.find((d) => d._id === formData.doctorId);
      const selectedDept = departments.find((d) => d._id === formData.departmentId);

      const payload = {
        visitType: formData.visitType,
        description: sanitizeText(formData.description),
        departmentId: formData.departmentId,
        doctorId: formData.doctorId,
        consultingDoctorId: formData.doctorId,
        consultingDoctor: selectedDoctor
          ? (selectedDoctor.name ||
             `${selectedDoctor.firstName || selectedDoctor.f_name || ""} ${selectedDoctor.lastName || selectedDoctor.l_name || ""}`.trim() ||
             selectedDoctor.email || "")
          : "",
        department: selectedDept ? selectedDept.name : "",
      };

      const result = await addVisit(patientId, payload);
      if (onVisitCreated) onVisitCreated(result);
      onClose();
    } catch (err) {
      console.error("Error creating visit:", err);
      setError(err?.response?.data?.message || err.message || "Failed to create visit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 1050,
        backgroundColor: "rgba(17, 24, 39, 0.4)", // Darker semi-transparent background
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: "#fff", borderRadius: "16px", width: "420px", maxWidth: "95vw", maxHeight: "95vh", overflow: "auto", boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" }}>
        {/* Header */}
        <div style={{ padding: "24px 24px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h5 style={{ margin: 0, color: "#1E3A8A", fontWeight: 700, fontSize: "20px" }}>Create Patients Visit</h5>
          <button onClick={onClose} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", width: 24, height: 24 }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Search Bar matching mockup exactly */}
        <div style={{ padding: "0 24px", display: "flex", gap: "12px", marginBottom: "8px" }}>
          <div style={{ position: "relative", flex: 1 }}>
            <div style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", display: "flex" }}>
              <SearchIcon />
            </div>
            <input 
              type="text" 
              placeholder="Search" 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: "100%", padding: "12px 36px", border: "1.5px solid #E5E7EB", borderRadius: "8px", fontSize: "14px", outline: "none", color: "#374151", boxSizing: "border-box" }}
              onFocus={(e) => Object.assign(e.target.style, { borderColor: "#818cf8", boxShadow: "0 0 0 3px rgba(129, 140, 248, 0.2)" })}
              onBlur={(e) => Object.assign(e.target.style, { borderColor: "#E5E7EB", boxShadow: "none" })}
            />
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm("")}
                style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", border: "none", background: "none", display: "flex", cursor: "pointer", padding: 0 }}
              >
                <CloseIconSmall />
              </button>
            )}
          </div>
            {/* <button style={{ width: "45px", height: "45px", background: "#2563EB", border: "none", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
              <SearchIconWhite />
            </button> */}
        </div>

        {/* Patient Info Card */}
        <div style={{ margin: "16px 24px 24px", padding: "16px", background: "#EFF6FF", borderRadius: "12px", display: "flex", alignItems: "center", gap: "16px" }}>
          <div style={{ width: "56px", height: "56px", borderRadius: "50%", background: "#6366F1", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "600", fontSize: "20px", flexShrink: 0 }}>
            {patientName ? patientName.split(' ').map(n=>n[0]).join('').substring(0,2).toUpperCase() : "AS"}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: "16px", color: "#1F2937", marginBottom: "4px" }}>{patientName || "Arjun Sharma"}</div>
            <div style={{ fontSize: "13px", color: "#6B7280", lineHeight: "1.4" }}>{patientMeta && patientMeta !== "" ? patientMeta.split('\n').map((line, i) => <div key={i}>{line}</div>) : "M: 39yr / 9876543210 / Bangalore\nLast Visit: 11 Mar 2026 (5 Days ago)"}</div>
          </div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <RightArrow />
          </div>
        </div>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column" }}>
          <div style={{ padding: "0 24px" }}>
            {!canAddVisit && (
              <div style={{ background: "#EBF5FF", border: "1px solid #B4C6FC", borderRadius: "8px", padding: "10px 14px", marginBottom: "16px", color: "#1E429F", fontSize: "13px" }}>
                <strong>Read-Only Mode:</strong> You do not have permission to create patient visits.
              </div>
            )}
            <fieldset disabled={!canAddVisit} style={{ border: "none", padding: 0, margin: 0, opacity: !canAddVisit ? 0.88 : 1 }}>
            {error && (
              <div style={{ background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: "8px", padding: "10px 14px", marginBottom: "16px", color: "#DC2626", fontSize: "13px" }}>
                {error}
              </div>
            )}

            {/* Visit Type */}
            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", fontWeight: 600, color: "#374151", marginBottom: "8px", fontSize: "14px" }}>Visit Type</label>
              <select
                name="visitType"
                value={formData.visitType}
                onChange={handleChange}
                style={{ width: "100%", padding: "12px 14px", border: "1px solid #D1D5DB", borderRadius: "8px", fontSize: "14px", color: formData.visitType ? "#1F2937" : "#9CA3AF", appearance: "none", background: "#fff url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%236B7280' d='M6 8L0 0h12z'/%3E%3C/svg%3E\") no-repeat right 16px center", outline: "none", boxSizing: "border-box" }}
                onFocus={(e) => Object.assign(e.target.style, { borderColor: "#818cf8", boxShadow: "0 0 0 3px rgba(129, 140, 248, 0.2)" })}
                onBlur={(e) => Object.assign(e.target.style, { borderColor: "#D1D5DB", boxShadow: "none" })}
                required
              >
                <option value="" disabled hidden>Select</option>
                {visitTypes.map((t) => (
                  <option key={t} value={t} style={{ color: "#1F2937" }}>{t}</option>
                ))}
              </select>
            </div>

            {/* Description */}
            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", fontWeight: 600, color: "#374151", marginBottom: "8px", fontSize: "14px" }}>Description</label>
              <input
                type="text"
                name="description"
                value={formData.description}
                onChange={handleChange}
                style={{ width: "100%", padding: "12px 14px", border: "1px solid #D1D5DB", borderRadius: "8px", fontSize: "14px", color: "#1F2937", boxSizing: "border-box", outline: "none" }}
                onFocus={(e) => Object.assign(e.target.style, { borderColor: "#818cf8", boxShadow: "0 0 0 3px rgba(129, 140, 248, 0.2)" })}
                onBlur={(e) => Object.assign(e.target.style, { borderColor: "#D1D5DB", boxShadow: "none" })}
              />
            </div>

            {/* Department */}
            <div style={{ marginBottom: "20px" }}>
              <label style={{ display: "block", fontWeight: 600, color: "#374151", marginBottom: "8px", fontSize: "14px" }}>Department</label>
              <select
                name="departmentId"
                value={formData.departmentId}
                onChange={handleChange}
                disabled={loadingDepts}
                style={{ width: "100%", padding: "12px 14px", border: "1px solid #D1D5DB", borderRadius: "8px", fontSize: "14px", color: formData.departmentId ? "#1F2937" : "#9CA3AF", appearance: "none", background: "#fff url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%236B7280' d='M6 8L0 0h12z'/%3E%3C/svg%3E\") no-repeat right 16px center", outline: "none", boxSizing: "border-box" }}
                onFocus={(e) => Object.assign(e.target.style, { borderColor: "#818cf8", boxShadow: "0 0 0 3px rgba(129, 140, 248, 0.2)" })}
                onBlur={(e) => Object.assign(e.target.style, { borderColor: "#D1D5DB", boxShadow: "none" })}
                required
              >
                <option value="" disabled hidden>{loadingDepts ? "Loading..." : "Select"}</option>
                {departments.map((dept) => (
                  <option key={dept._id} value={dept._id} style={{ color: "#1F2937" }}>{dept.name}</option>
                ))}
              </select>
            </div>

            {/* Doctor / Nurse */}
            <div style={{ marginBottom: "24px" }}>
              <label style={{ display: "block", fontWeight: 600, color: "#374151", marginBottom: "8px", fontSize: "14px" }}>Doctor/ Nurse</label>
              <select
                name="doctorId"
                value={formData.doctorId}
                onChange={handleChange}
                disabled={loadingDoctors}
                style={{ width: "100%", padding: "12px 14px", border: "1px solid #D1D5DB", borderRadius: "8px", fontSize: "14px", color: formData.doctorId ? "#1F2937" : "#9CA3AF", appearance: "none", background: "#fff url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%236B7280' d='M6 8L0 0h12z'/%3E%3C/svg%3E\") no-repeat right 16px center", outline: "none", boxSizing: "border-box", opacity: (loadingDoctors) ? 0.7 : 1 }}
                onFocus={(e) => Object.assign(e.target.style, { borderColor: "#818cf8", boxShadow: "0 0 0 3px rgba(129, 140, 248, 0.2)" })}
                onBlur={(e) => Object.assign(e.target.style, { borderColor: "#D1D5DB", boxShadow: "none" })}
                required
              >
                <option value="" disabled hidden>
                  {loadingDoctors
                    ? "Loading..."
                    : doctors.length === 0
                    ? "No doctors found"
                    : "Select"}
                </option>
                {doctors.map((doc) => {
                  const docName =
                    doc.name ||
                    (`${doc.firstName || doc.f_name || ""} ${doc.lastName || doc.l_name || ""}`).trim() ||
                    doc.email ||
                    "Unknown";
                  return (
                    <option key={doc._id} value={doc._id} style={{ color: "#1F2937" }}>
                      {docName}
                    </option>
                  );
                })}
              </select>
            </div>
            </fieldset>
          </div>

          {/* Footer */}
          <div style={{ padding: "0 24px 24px", display: "flex", gap: "16px" }}>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              onMouseOver={(e) => Object.assign(e.target.style, { background: "#F3F4F6", borderColor: "#D1D5DB" })}
              onMouseOut={(e) => Object.assign(e.target.style, { background: "#FFF", borderColor: "#E5E7EB" })}
              style={{ flex: 1, padding: "12px", borderRadius: "8px", border: "1px solid #E5E7EB", background: "#FFF", color: "#374151", cursor: "pointer", fontSize: "15px", fontWeight: 600, transition: "0.2s" }}
            >
              {canAddVisit ? "Cancel" : "Close"}
            </button>
            {canAddVisit && (
              <button
                type="submit"
                disabled={submitting || loadingDepts}
                onMouseOver={(e) => Object.assign(e.target.style, { background: "#1D4ED8" })}
                onMouseOut={(e) => Object.assign(e.target.style, { background: "#2563EB" })}
                style={{ flex: 1, padding: "12px", borderRadius: "8px", border: "none", background: "#2563EB", color: "#fff", cursor: "pointer", fontSize: "15px", fontWeight: 600, transition: "0.2s" }}
              >
                {submitting ? "Creating..." : "Create Visit"}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateVisitModal;
